import { MDXProvider } from "@mdx-js/react";
import { NativeSelect } from "@replicate/ui";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  Navigate,
  Outlet,
  RouterProvider,
  createBrowserRouter,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  formatNodeInput,
  formatPythonInput,
  getFileInputs,
  indent,
  supportsStreaming,
} from "../../code-snippets";
import { getInputSchema, getOutputSchema } from "../../schema";
import type {
  AccessToken,
  CogInputPropertyValue,
  CogInputSchema,
  CogOutputValue,
  Model,
  Prediction,
  Version,
} from "../../types";
import { RunWithContext } from "../api-playground/run-with";
import {
  cleanInputForSubmission,
  processInputForSubmission,
} from "../api-playground/util";
import { IfFlag } from "../flags/if-flag";
import icons from "../icons";
import { RunDialogContext, useRunDialogContext } from "./context";
import { LanguagePicker } from "./language-picker";
import { GetStartedPage } from "./pages/get-started";
import { LearnMorePage } from "./pages/learn-more";
import { ModelAPIPage } from "./pages/model-api";
import { OpenAIPage } from "./pages/openai";
import { PredictionAPIPage } from "./pages/prediction-api";
import { GettingStartedContext, MDXPre, MDXWrapper } from "./snippets";
import { TabNavLink } from "./tab-nav-link";

export interface OperationDefinition {
  operation_id: string;
  slug: string;
  method: "get" | "post" | "patch" | "delete";
  path: string;
  description: string;
  summary: string;
  parameters: {
    $ref?: string;
    in: string;
    type?: string;
    description?: string;
    name?: string;
    required?: boolean;
    schema?: any;
  }[];
  request_body: any;
}

export interface RunDialogPageProps {
  example: Prediction;
  model: Model;
  operations: Record<string, OperationDefinition>;
  token: AccessToken;
  usesVersionlessApi: boolean;
  version: Version;
}

export default function RunDialog(props: RunDialogPageProps) {
  const { model } = props;
  const router = createBrowserRouter(
    [
      {
        path: "/",
        element: <Layout {...props} />,
        children: [
          {
            index: true,
            element: <GetStartedPage {...props} />,
          },
          {
            path: "learn-more",
            element: <LearnMorePage {...props} />,
          },
          {
            path: "schema",
            element: <ModelAPIPage {...props} />,
          },
          {
            path: "api-reference",
            element: <PredictionAPIPage {...props} />,
          },
          {
            path: "openai",
            element: <OpenAIPage {...props} />,
          },
          {
            path: "*",
            element: <Navigate to="/" replace />,
          },
        ],
      },
    ],
    {
      basename: `/${model.owner}/${model.name}/api`,
    }
  );

  return <RouterProvider router={router} />;
}

function Layout({
  example,
  model,
  token,
  usesVersionlessApi,
  version,
}: RunDialogPageProps) {
  const mainContentRef = useRef<HTMLDivElement>(null);
  const [language, setLanguage] = useState<RunWithContext>(
    RunWithContext.NodeJS
  );

  const mdxComponents = useMemo(() => {
    return {
      wrapper: MDXWrapper,
      pre: MDXPre,
    };
  }, []);

  const gsp = gettingStartedProps({
    input: example.input,
    output: example.output,
    version,
    model,
    language,
    usesVersionlessApi,
  });

  const location = useLocation();

  // Update document title based on current route
  useEffect(() => {
    const baseModelName = `${model.owner}/${model.name}`;

    let pageTitle: string;
    switch (location.pathname) {
      case "/":
        pageTitle = `${baseModelName} | Get started`;
        break;
      case "/learn-more":
        pageTitle = `${baseModelName} | Learn more`;
        break;
      case "/schema":
        pageTitle = `${baseModelName} | Schema`;
        break;
      case "/api-reference":
        pageTitle = `${baseModelName} | API reference`;
        break;
      default:
        pageTitle = `${baseModelName} | Run`;
        break;
    }

    document.title = pageTitle;
  }, [location.pathname, model.owner, model.name]);

  useLayoutEffect(() => {
    const { hash } = new URL(window.location.href);

    if (hash) {
      // The page mounted with a hash in the URL.
      const hashEl = document.getElementById(hash.slice(1));
      if (!hashEl) return;
      setTimeout(() => {
        hashEl.scrollIntoView({ block: "start" });
      });
    }
  }, []);

  useEffect(() => {
    if (location.key === "default") {
      return;
    }

    // Anytime the "location" changes, we want to scroll the page to where the new route begins.
    if (!mainContentRef.current) return;

    const top =
      document.documentElement.scrollTop - document.documentElement.clientTop;
    if (top > mainContentRef.current.getBoundingClientRect().top) {
      mainContentRef.current.scrollIntoView({ block: "start" });
    }
  }, [location]);

  const headerEl = (
    <div className="z-20 flex">
      <div className="flex w-full flex-shrink-0 items-center justify-between">
        <p className="font-heading text-r8-2xl">
          Run{" "}
          <span className="font-semibold">
            {version._extras.model.owner}/{version._extras.model.name}
          </span>{" "}
          with an API
        </p>
      </div>
    </div>
  );

  const trackAndSetLanguage = (language: RunWithContext) => {
    setLanguage(language);
  };

  return (
    <div className="flex flex-col lg:flex-row h-full gap-4 lg:gap-8">
      <div className="mb-0 lg:hidden">{headerEl}</div>
      <aside className="lg:w-64 flex-shrink-0 min-w-0">
        <div className="lg:sticky top-24">
          <div className="pt-1 pb-3">
            <p className="uppercase text-r8-gray-12 text-r8-xs font-semibold tracking-wider">
              Table of Contents
            </p>
          </div>
          <div className="mb-4">
            <LanguagePicker
              value={language}
              onChange={(lang) => {
                trackAndSetLanguage(lang);
              }}
            />
          </div>
          <div className="block lg:hidden">
            <MobileTabNav />
          </div>
          <div className="hidden lg:flex flex-col lg:gap-2">
            <TabNavLink to="">Get started</TabNavLink>
            <TabNavLink to="learn-more">Learn more</TabNavLink>
            <TabNavLink to="schema">Schema</TabNavLink>
            <TabNavLink to="api-reference">API reference</TabNavLink>
            {/* FIXME: Temporarily showing OpenAI API instructions for llama models only */}
            {usesVersionlessApi && model.name.includes("llama") && (
              <IfFlag flag="show-open-ai-api-instructions">
                <TabNavLink to="openai">OpenAI API</TabNavLink>
              </IfFlag>
            )}
          </div>
        </div>
      </aside>
      <div ref={mainContentRef} className="flex-1 min-w-0 scroll-mt-28">
        <MDXProvider components={mdxComponents}>
          <RunDialogContext.Provider
            value={{
              language,
              setLanguage: trackAndSetLanguage,
              model,
              version,
              token,
            }}
          >
            <GettingStartedContext.Provider value={gsp}>
              <Outlet />
            </GettingStartedContext.Provider>
          </RunDialogContext.Provider>
        </MDXProvider>
      </div>
    </div>
  );
}

function MobileTabNav() {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div>
      <NativeSelect
        id="mobile-tab-nav"
        name="mobile-tab-nav"
        value={location.pathname}
        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
          navigate(e.target.value);
        }}
      >
        <option value="">Get started</option>
        <option value="/learn-more">Learn more</option>
        <option value="/schema">Schema</option>
        <option value="/api-reference">API reference</option>
        <IfFlag flag="show-open-ai-api-instructions">
          <option value="/openai">OpenAI API</option>
        </IfFlag>
      </NativeSelect>
    </div>
  );
}

export function gettingStartedProps({
  input,
  language,
  model,
  output,
  usesVersionlessApi,
  version,
}: {
  input: Record<string, CogInputPropertyValue>;
  language: RunWithContext;
  model: Model;
  output: CogOutputValue | null;
  usesVersionlessApi: boolean;
  version: Version;
}) {
  const schema = getInputSchema(version._extras).properties;
  const cleanedInput = cleanInputForSubmission(input, schema, {
    hideDefaultValues: true,
  });
  const processedInput = processInputForSubmission(cleanedInput, schema);

  const { owner, name } = version._extras.model;

  const streaming = supportsStreaming(version._extras);

  const hasFileInputOutput =
    hasFileOutput(version, output) || hasFileOutputArray(version, output);

  const printOutput = (() => {
    if (hasFileOutput(version, output)) {
      return writeFileOutput(language, version, output);
    }

    if (hasFileOutputArray(version, output)) {
      return writeFileOutputArray(language, version, output);
    }

    switch (language) {
      case "http":
        return "";
      case "nodejs":
        switch (streaming) {
          case "none":
            return "console.log(output)";
          case "entry":
            return `console.log(output.join("\n"));`;
          case "concatenate":
            return `console.log(output.join(""));`;
          default:
            throw Error("NotImplemented");
        }
      case "python":
        switch (streaming) {
          case "none":
            return "print(output)";
          case "entry":
            return `print("\n".join(output))`;
          case "concatenate":
            return `print("".join(output))`;
          default:
            throw Error("NotImplemented");
        }
    }
  })();

  const printStreamEvent = (() => {
    switch (language) {
      case "http":
        return "";
      case "nodejs":
        switch (streaming) {
          case "none":
            return "console.log(output)";
          case "entry":
            return "process.stdout.write(`${event}\n`)";
          case "concatenate":
            return "process.stdout.write(`${event}`)";
          default:
            throw Error("NotImplemented");
        }
      case "python":
        switch (streaming) {
          case "none":
            return "print(event)";
          case "entry":
            return "print(event)";
          case "concatenate":
            return `print(event, end="")`;
          default:
            throw Error("NotImplemented");
        }
    }
  })();

  const fileInputFields = getFileInputs(version._extras);

  // FIXME: "input" is a reserved word in Python...
  const firstFileInputField = fileInputFields[0] ?? "input";
  const firstFileInputValue =
    input[firstFileInputField] ??
    `https://example.com/path/to/${exampleFileName(firstFileInputField, "")}`;
  const firstFileInputExample = exampleFileName(
    firstFileInputField,
    firstFileInputValue
  );

  const formattedInput = formatInput(language, processedInput, schema);
  const inputText = indent(formattedInput, {
    first: 0,
    inner: 4,
    last: 0,
  });

  const formattedInputWithFile = formatInput(
    language,
    { ...processedInput, [firstFileInputField]: "{{ input }}" },
    schema
  );
  const inputWithFileText = indent(
    formattedInputWithFile.replace(`"{{ input }}"`, firstFileInputField),
    {
      first: 0,
      inner: 4,
      last: 0,
    }
  );
  const inputWithFileAsBashVariable = indent(
    formattedInputWithFile.replace(
      `"{{ input }}"`,
      `"$${firstFileInputField}"`
    ),
    {
      first: 0,
      inner: 2,
      last: 0,
    }
  );

  let outputText = (() => {
    if (hasFileOutput(version, output)) {
      const ext = extractFileExtension(`${output}` ?? "");
      return `output${ext} written to disk`;
    }

    if (hasFileOutputArray(version, output)) {
      if (Array.isArray(output)) {
        const ext = extractFileExtension(output ? output[0] : "");
        const filenames = output.map(
          (_: unknown, idx: number) => `output_${idx}${ext}`
        );
        return `${filenames.join(", ")} written to disk`;
      }
      return "file output written to disk";
    }

    switch (streaming) {
      case "none":
        return JSON.stringify(output);
      case "entry":
        return JSON.stringify(
          Array.isArray(output) ? output.join("\n") : output
        );
      case "concatenate":
        return JSON.stringify(Array.isArray(output) ? output.join("") : output);
      default:
        throw Error("NotImplemented");
    }
  })();

  const outputStreamEvent = (() => {
    switch (streaming) {
      case "none":
        return JSON.stringify(output);
      case "entry":
        return JSON.stringify(
          Array.isArray(output) && output.length ? output[0] : output
        );
      case "concatenate":
        return JSON.stringify(
          Array.isArray(output) && output.length ? output[0] : output
        );
      default:
        throw Error("NotImplemented");
    }
  })();

  if (outputText.length > 60) {
    outputText = `${outputText.slice(0, 57)}...`;
  }

  return {
    fileLocalValue: firstFileInputExample,
    fileInputField: firstFileInputField,
    fileInputValue: firstFileInputValue,
    hasFileInputs: fileInputFields.length > 0,
    input: inputText,
    inputWithFile: inputWithFileText,
    inputWithFileAsBashVariable,
    output: outputText,
    outputStreamEvent,
    predictionApiEndpoint: usesVersionlessApi
      ? `https://api.replicate.com/v1/models/${model.owner}/${model.name}/predictions`
      : "https://api.replicate.com/v1/predictions",
    printOutput,
    printStreamEvent,
    runnableFieldName: usesVersionlessApi ? "model" : "version",
    runnableFieldValue: usesVersionlessApi
      ? `${model.owner}/${model.name}`
      : `${version.id}`,
    runnableName: usesVersionlessApi
      ? `${model.owner}/${model.name}`
      : `${owner}/${name}:${version.id}`,
    modelDisplayName: `${model.owner}/${model.name}`,
    streaming,
    usesVersionlessApi,
    conditionalFsNodeImport: hasFileInputOutput
      ? `import { writeFile } from "fs/promises";`
      : "",
  };
}

function formatInput(
  language: RunWithContext,
  input: object,
  schema: CogInputSchema["properties"]
) {
  switch (language) {
    case "http":
      return indent(JSON.stringify(input, null, 2), {
        first: 0,
        inner: 0,
        last: 1,
        character: "\t",
      });
    case "python":
      return indent(formatPythonInput(input, schema), 0);
    case "nodejs":
      return indent(formatNodeInput(input, schema), 0);
    default:
      throw new Error("NotImplemented");
  }
}

function extractFileExtension(value: string): string {
  try {
    const url = new URL(value);
    const parts = url.pathname.split(".");
    if (parts.length > 1) {
      return `.${parts.pop()}`;
    }
  } catch {}

  return "";
}

function exampleFileName(fieldName: string, value: CogInputPropertyValue) {
  if (fieldName === "image" || fieldName === "mask") {
    return `${fieldName}.png`;
  }
  if (fieldName === "audio") {
    return `${fieldName}.wav`;
  }

  if (typeof value === "string") {
    const ext = extractFileExtension(value);
    return `${fieldName}${ext}`;
  }

  return `${fieldName}`;
}

export function ClientLibraryPicker() {
  const { language, setLanguage } = useRunDialogContext();

  return (
    <div className="flex flex-row gap-4 mb-4">
      <button
        type="button"
        data-language={RunWithContext.NodeJS}
        data-selected={language === RunWithContext.NodeJS}
        onClick={() => {
          setLanguage(RunWithContext.NodeJS);
        }}
        className="px-2 py-4 w-28 block underline hover:bg-r8-gray-1 transition-colors text-r8-gray-12 group h-full hover:ring-1 hover:ring-r8-gray-6 data-[selected=true]:bg-r8-gray-2 data-[selected=true]:ring-r8-gray-12 data-[selected=true]:ring-1"
      >
        <div className="flex flex-col items-center justify-center gap-2 h-full">
          <div className="w-6 h-6">
            <icons.languages.NodeJSIcon />
          </div>
          <span className="text-r8-sm font-sans">Node.js</span>
        </div>
      </button>
      <button
        type="button"
        data-language={RunWithContext.Python}
        data-selected={language === RunWithContext.Python}
        onClick={() => {
          setLanguage(RunWithContext.Python);
        }}
        className="px-2 py-4 w-28 block underline hover:bg-r8-gray-1 transition-colors text-r8-gray-12 group h-full hover:ring-1 hover:ring-r8-gray-6 data-[selected=true]:bg-r8-gray-2 data-[selected=true]:ring-r8-gray-12 data-[selected=true]:ring-1"
      >
        <div className="flex flex-col items-center justify-center gap-2 h-full">
          <div className="w-6 h-6">
            <icons.languages.PythonIcon />
          </div>
          <span className="text-r8-sm font-sans">Python</span>
        </div>
      </button>
      <button
        type="button"
        data-language={RunWithContext.HTTP}
        data-selected={language === RunWithContext.HTTP}
        onClick={() => {
          setLanguage(RunWithContext.HTTP);
        }}
        className="px-2 py-4 w-28 block underline hover:bg-r8-gray-1 transition-colors text-r8-gray-12 group h-full hover:ring-1 hover:ring-r8-gray-6 data-[selected=true]:bg-r8-gray-2 data-[selected=true]:ring-r8-gray-12 data-[selected=true]:ring-1"
      >
        <div className="flex flex-col items-center justify-center gap-2 h-full">
          <div className="w-6 h-6 text-[#073551] group-focus:text-r8-gray-1 group-active:text-r8-gray-1">
            <icons.languages.CurlIcon />
          </div>
          <span className="text-r8-sm font-sans">HTTP</span>
        </div>
      </button>
    </div>
  );
}

function hasFileOutput(
  version: Version,
  _output: unknown
): _output is string | null {
  const schema = getOutputSchema(version._extras);
  if ("type" in schema && schema.type === "string" && schema.format === "uri") {
    return true;
  }
  return false;
}

function hasFileOutputArray(
  version: Version,
  _output: unknown
): _output is string[] | null {
  const schema = getOutputSchema(version._extras);
  if (
    "type" in schema &&
    schema.type === "array" &&
    schema.items.type === "string" &&
    schema.items.format === "uri"
  ) {
    return true;
  }
  return false;
}

function writeFileOutput(
  language: RunWithContext,
  _version: Version,
  output: string | null | undefined
): string {
  const ext = extractFileExtension(output ?? "");

  switch (language) {
    case "http":
      return "";
    case "nodejs":
      return `// To access the file URL:
console.log(output.url());
//=> "https://replicate.delivery/.../output${ext}"

// To write the file to disk:
await writeFile("output${ext}", output);`; // No trim() to add whitespace above `import`
    case "python":
      return `
# To access the file URL:
print(output.url())
#=> "https://replicate.delivery/.../output${ext}"

# To write the file to disk:
with open("output${ext}", "wb") as file:
    file.write(output.read())
`.trim();
  }
  return "";
}

function writeFileOutputArray(
  language: RunWithContext,
  _version: Version,
  output: string[] | null | undefined
): string {
  const url = output ? output[0] ?? "" : "";
  const ext = extractFileExtension(url);

  switch (language) {
    case "http":
      return "";
    case "nodejs":
      return `// To access the file URLs:
console.log(output[0].url());
//=> "https://replicate.delivery/.../output_0${ext}"

// To write the files to disk:
for (const [index, item] of Object.entries(output)) {
  await writeFile(\`output_\${index}${ext}\`, item);
}`; // No trim() to add whitespace above `import`
    case "python":
      return `
# To access the file URLs:
print(output[0].url())
#=> "https://replicate.delivery/.../output_0${ext}"

# To write the files to disk:
for index, item in enumerate(output):
    with open(f"output_\{index}${ext}", "wb") as file:
        file.write(item.read())
`.trim();
  }
  return "";
}
